import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import {
  CASE_CLIENT_ROLES,
  CASE_CLIENT_ROLE_LABELS,
  CASE_TYPES,
  CASE_TYPE_LABELS,
  type CaseListItem,
} from '@lawyer/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CaseDialog } from '../components/cases/CaseDialog'
import { ErrorAlert } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { useProjectOptions } from '../hooks/useLookups'
import { api } from '../lib/api'
import { formatDate } from '../lib/format'
import { qk } from '../lib/queryClient'

export function CasesPage() {
  const navigate = useNavigate()
  const projects = useProjectOptions()
  const [search, setSearch] = useState('')
  const [projectId, setProjectId] = useState('')
  const [caseType, setCaseType] = useState('')
  const [clientRole, setClientRole] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const params = {
    search: search || undefined,
    projectId: projectId || undefined,
    caseType: caseType || undefined,
    clientRole: clientRole || undefined,
  }

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: qk.cases(params),
    queryFn: () => api.get<CaseListItem[]>('/cases', params),
  })

  const columns: GridColDef<CaseListItem>[] = [
    {
      field: 'name',
      headerName: 'תיק',
      flex: 2,
      minWidth: 200,
      renderCell: (params_) => (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => navigate(`/cases/${params_.row.id}`)}
          sx={{ font: 'inherit', color: 'inherit' }}
        >
          {params_.row.name}
        </Link>
      ),
    },
    {
      field: 'caseType',
      headerName: 'סוג',
      width: 100,
      renderCell: (params_) => (
        <Chip size="small" label={CASE_TYPE_LABELS[params_.row.caseType]} />
      ),
    },
    {
      field: 'clientRole',
      headerName: 'צד',
      width: 90,
      renderCell: (params_) => (
        <Chip size="small" variant="outlined" label={CASE_CLIENT_ROLE_LABELS[params_.row.clientRole]} />
      ),
    },
    {
      field: 'clientNames',
      headerName: 'לקוחות',
      flex: 2,
      minWidth: 180,
      valueGetter: (_value, row) => row.clientNames.join(', '),
    },
    { field: 'projectName', headerName: 'פרויקט', flex: 1, minWidth: 140 },
    {
      field: 'transactionDate',
      headerName: 'תאריך עסקה',
      width: 120,
      valueGetter: (_value, row) => (row.transactionDate ? formatDate(row.transactionDate) : '—'),
    },
    {
      field: 'address',
      headerName: 'כתובת',
      flex: 1.5,
      minWidth: 160,
      valueGetter: (_value, row) => {
        // A case inside a project has no street or city of its own — only the unit.
        const place = [row.street, row.city].filter(Boolean).join(' ') || row.projectAddress
        const unit = [row.buildingNo, row.aptNo && `דירה ${row.aptNo}`].filter(Boolean).join(' ')
        return [place, unit].filter(Boolean).join(' ')
      },
    },
    { field: 'openTaskCount', headerName: 'משימות פתוחות', width: 130, type: 'number' },
  ]

  return (
    <>
      <PageHeader
        title="תיקים"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            תיק חדש
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              label="חיפוש"
              placeholder="שם, תיאור או כתובת"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              select
              label="פרויקט"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {(projects.data ?? []).map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField
              select
              label="סוג"
              value={caseType}
              onChange={(e) => setCaseType(e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {CASE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {CASE_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField
              select
              label="צד"
              value={clientRole}
              onChange={(e) => setClientRole(e.target.value)}
            >
              <MenuItem value="">הכל</MenuItem>
              {CASE_CLIENT_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {CASE_CLIENT_ROLE_LABELS[role]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}

      <Stack sx={{ height: 'calc(100vh - 380px)', minHeight: 380 }}>
        <Paper variant="outlined" sx={{ height: '100%' }}>
          <DataGrid
            rows={data ?? []}
            columns={columns}
            loading={isFetching}
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            pageSizeOptions={[25, 50, 100]}
            sx={{ border: 0 }}
          />
        </Paper>
      </Stack>

      <CaseDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => navigate(`/cases/${created.id}`)}
      />
    </>
  )
}
